import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  LayoutDashboard,
  CheckSquare,
  FolderKanban,
  Calendar,
  Users,
  ClipboardCheck,
  Settings,
  Bell,
  ChevronLeft,
  ChevronRight,
  Timer,
  LogOut,
  User,
  Send,
  Building2,
  Bug,
  FileText,
  Ticket,
} from 'lucide-react';

import { useAppSelector, useAppDispatch } from '@/store';
import { selectCurrentUser, logout } from '@/store/slices/authSlice';
import { selectSidebarView, toggleSidebar } from '@/store/slices/uiSlice';
import { logout as apiLogout } from '@/services/api/authService';
import { selectActiveTimers, pauseAllTimersAsync, selectTimerStartError, clearTimerStartError } from '@/store/slices/timerSlice';
import { useUnreadCount } from '@/hooks/useNotifications';
import NotificationsDrawer from '@/shared/components/NotificationsDrawer';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { UserRole } from '@/types';
import { normalizeRole, isAdminOrAbove, type EffectiveRole } from '@/shared/utils/roles';
import { usePermissions } from '@/shared/contexts/PermissionsContext';
import { PATH_TO_PAGE_KEY, shouldEnforcePagePermissions } from '@/shared/utils/pageAccess';
import ActiveTimerBar from './ActiveTimerBar';
import GlobalModals from './GlobalModals';
import MobileBottomNav from './MobileBottomNav';
import MobileMoreMenu from './MobileMoreMenu';
import MobileHeader from './MobileHeader';
import { getPageHeader } from '@/shared/config/pageHeaders';
import { useTimerTick } from '@/hooks/useTimerTick';
import ReportIssueModal from './modals/ReportIssueModal';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';

const DRAWER_WIDTH = 260;
const COLLAPSED_DRAWER_WIDTH = 72;

const MANAGEMENT_PATHS = ['/team', '/departments', '/reviews', '/users', '/settings'];

// Navigation items with role-based visibility
const getNavigationItems = (role: EffectiveRole) => {
  const baseItems = [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      roles: 'all',
    },
    {
      path: '/tasks',
      label: 'My Tasks',
      icon: CheckSquare,
      roles: 'all',
    },
    {
      path: '/delegated',
      label: 'Delegated Tasks',
      icon: Send,
      roles: 'all',
    },
    {
      path: '/projects',
      label: 'Projects',
      icon: FolderKanban,
      roles: 'all',
    },
    {
      path: '/tickets',
      label: 'Tickets',
      icon: Ticket,
      roles: 'all',
    },
    {
      path: '/calendar',
      label: 'Calendar',
      icon: Calendar,
      roles: 'all',
    },
    {
      path: '/documents',
      label: 'Documents',
      icon: FileText,
      roles: 'all',
    },
  ];

  const managerItems = [
    {
      path: '/team',
      label: 'Team',
      icon: Users,
      roles: ['super_admin', 'admin', 'manager'],
    },
    {
      path: '/departments',
      label: 'Departments',
      icon: Building2,
      roles: ['super_admin', 'admin', 'manager'],
    },
    {
      path: '/reviews',
      label: 'Reviews & Approval',
      icon: ClipboardCheck,
      roles: ['super_admin', 'admin', 'manager'],
    },
  ];

  const adminItems = [
    {
      path: '/users',
      label: 'Users',
      icon: User,
      roles: ['super_admin', 'admin'],
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: Settings,
      roles: ['super_admin', 'admin'],
    },
  ];

  const allItems = [...baseItems, ...managerItems, ...adminItems];

  return allItems.filter((item) => {
    if (item.roles === 'all') return true;
    return (item.roles as EffectiveRole[]).includes(role);
  });
};

/**
 * Layout Component
 * Main application layout with sidebar navigation and header
 * Implements role-based navigation visibility
 * Mobile-first responsive design with bottom navigation
 */
const Layout: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useAppSelector(selectCurrentUser);
  usePresenceHeartbeat(!!user?.id);
  const activeTimers = useAppSelector(selectActiveTimers);
  const timerStartError = useAppSelector(selectTimerStartError);
  const sidebarView = useAppSelector(selectSidebarView);
  const { showError } = useSnackbar();
  const isCollapsed = !isMobile && sidebarView === 'collapsed';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const [reportIssueOpen, setReportIssueOpen] = useState(false);
  // State for logout confirmation dialog when active timers are running
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  // State to prevent multiple logout attempts during timer pause operation
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showTimerInHeader, setShowTimerInHeader] = useState(true);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('userPreferences');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.showTimerInHeader !== undefined) {
          setShowTimerInHeader(parsed.showTimerInHeader);
        }
      }
    } catch {
      // Ignore invalid preferences
    }
  }, [location.pathname]);

  // Fetch unread notification count
  const { data: unreadCount = 0 } = useUnreadCount();

  const drawerWidth = isCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;
  const pageHeader = getPageHeader(location.pathname);

  useTimerTick();

  useEffect(() => {
    if (timerStartError) {
      showError(timerStartError);
      dispatch(clearTimerStartError());
    }
  }, [timerStartError, showError, dispatch]);

  const effectiveRole = normalizeRole(user?.role || UserRole.INDIVIDUAL_CONTRIBUTOR);
  const { canAccessPage, isPermissionsReady } = usePermissions();

  const navigationItems = getNavigationItems(effectiveRole).filter((item) => {
    // Admin / super_admin: full nav for now
    if (isAdminOrAbove(user?.role)) return true;
    // Non-engineers (e.g. manager): keep hardcoded role filter only
    if (!shouldEnforcePagePermissions(effectiveRole)) return true;
    // Engineers: Permissions Manager page access
    const pageKey = PATH_TO_PAGE_KEY[item.path];
    if (!pageKey) return true;
    if (!isPermissionsReady) return false;
    return canAccessPage(pageKey);
  });
  const mainNavItems = navigationItems.filter(
    (item) => item.path !== '/settings' && !MANAGEMENT_PATHS.includes(item.path)
  );
  const managementNavItems = navigationItems.filter((item) =>
    MANAGEMENT_PATHS.includes(item.path)
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    if (isMobile) {
      handleDrawerToggle();
      return;
    }
    dispatch(toggleSidebar());
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActivePath = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const renderNavItem = (item: (typeof navigationItems)[number]) => {
    const Icon = item.icon;
    const isActive = isActivePath(item.path);

    const button = (
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        sx={{
          borderRadius: '10px',
          minHeight: 42,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1 : 1.75,
          py: 1,
          bgcolor: isActive ? 'primary.main' : 'transparent',
          color: isActive ? 'primary.contrastText' : 'text.secondary',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: isActive
              ? 'primary.dark'
              : alpha(theme.palette.primary.main, 0.08),
            color: isActive ? 'primary.contrastText' : 'text.primary',
          },
          '& .MuiListItemIcon-root': {
            color: 'inherit',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: isCollapsed ? 0 : 36,
            justifyContent: 'center',
          }}
        >
          <Icon size={20} strokeWidth={isActive ? 2.25 : 2} />
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: '0.8125rem',
              fontWeight: isActive ? 600 : 500,
              noWrap: true,
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
        {isCollapsed ? (
          <Tooltip title={item.label} placement="right">
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'background.paper',
      }}
    >
      {/* Sidebar header with open/close control */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          minHeight: 64,
          px: isCollapsed ? 1 : 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {!isCollapsed && (
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            Task Management
          </Typography>
        )}
        <Tooltip
          title={isCollapsed ? 'Open sidebar' : 'Close sidebar'}
          placement="right"
        >
          <IconButton
            onClick={handleSidebarToggle}
            size="small"
            aria-label={isCollapsed ? 'Open sidebar' : 'Close sidebar'}
            sx={{ color: 'text.primary' }}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation */}
      <List
        sx={{
          flex: 1,
          px: isCollapsed ? 1 : 1.25,
          pt: 1.5,
          pb: 0.5,
          overflowY: 'auto',
        }}
      >
        {mainNavItems.map(renderNavItem)}

        {managementNavItems.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {managementNavItems.map(renderNavItem)}
          </>
        )}
      </List>

      {/* Footer */}
      <Box sx={{ px: isCollapsed ? 1 : 1.25, pb: 2, pt: 1 }}>
        <Divider sx={{ mb: 1.5 }} />

        {user && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              gap: 1.25,
              px: isCollapsed ? 0.5 : 1.5,
              py: 1.25,
              mb: 1,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.08),
              overflow: 'hidden',
              maxWidth: '100%',
            }}
          >
            <Tooltip
              title={isCollapsed ? (user.displayName || user.email || 'User') : ''}
              placement="right"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {user.firstName?.[0]?.toUpperCase() || user.displayName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </Tooltip>
            {!isCollapsed && (
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  title={user.displayName || 'User'}
                  sx={{ display: 'block' }}
                >
                  {user.displayName || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  title={user.email}
                  sx={{ display: 'block' }}
                >
                  {user.email}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {isAdminOrAbove(user?.role) && (
        <ListItem disablePadding>
          <Tooltip title={isCollapsed ? 'Settings' : ''} placement="right">
            <ListItemButton
              onClick={() => handleNavigation('/settings')}
              sx={{
                borderRadius: '10px',
                minHeight: 42,
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : 1.75,
                color: isActivePath('/settings') ? 'primary.main' : 'text.secondary',
                bgcolor: isActivePath('/settings')
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: 'text.primary',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: isCollapsed ? 0 : 36,
                  color: 'inherit',
                  justifyContent: 'center',
                }}
              >
                <Settings size={20} />
              </ListItemIcon>
              {!isCollapsed && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: isActivePath('/settings') ? 600 : 500,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        )}
      </Box>
    </Box>
  );

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles user logout action with active timer detection
   * 
   * Flow:
   * 1. Close profile menu
   * 2. Check if any timers are currently running (status === 'running')
   * 3. If running timers exist: Show confirmation dialog
   * 4. If no running timers: Proceed directly with logout
   * 
   * This prevents users from accidentally losing timer state on logout.
   * Paused timers don't trigger dialog since they're already persisted.
   */
  const handleLogout = async () => {
    handleProfileMenuClose();
    
    // Check if there are active running timers (paused timers are already saved)
    const runningTimers = activeTimers.filter(t => t.status === 'running');
    if (runningTimers.length > 0) {
      // Show confirmation dialog to user
      setShowLogoutDialog(true);
      return;
    }
    
    // No active running timers, proceed with standard logout
    await apiLogout();
    dispatch(logout());
    navigate('/login');
  };

  /**
   * Confirms logout after user acknowledges active timers
   * 
   * Flow:
   * 1. Set loading state to prevent duplicate logout attempts
   * 2. Pause all running timers via backend API (preserves timer state in DB)
   * 3. Call backend logout endpoint (invalidates JWT)
   * 4. Clear Redux auth state
   * 5. Navigate to login page
   * 
   * Error handling: If timer pause fails, still proceeds with logout
   * to prevent users from being stuck. Timer state may be lost in this case.
   */
  const confirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Pause all running timers before logout (saves to database)
      await dispatch(pauseAllTimersAsync()).unwrap();
      // Call backend logout endpoint
      await apiLogout();
      // Clear Redux authentication state
      dispatch(logout());
      // Redirect to login page
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still proceed with logout even if timer pause fails
      // This prevents users from being unable to logout
      await apiLogout();
      dispatch(logout());
      navigate('/login');
    } finally {
      // Reset loading state and close dialog
      setIsLoggingOut(false);
      setShowLogoutDialog(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Header - Only shown on mobile */}
      {isMobile && <MobileHeader onReportIssue={() => setReportIssueOpen(true)} />}

      {/* Desktop App Bar - Hidden on mobile */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { md: 64 } }}>
          {/* Page title */}
          <Box sx={{ minWidth: 0, mr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                lineHeight: 1.25,
                fontSize: '1.05rem',
              }}
              noWrap
            >
              {pageHeader.title}
            </Typography>
            {pageHeader.subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.3 }}
                noWrap
              >
                {pageHeader.subtitle}
              </Typography>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Active Timer Indicator */}
          {activeTimers.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mr: 2,
                px: 2,
                py: 0.5,
                bgcolor: 'success.50',
                borderRadius: '20px',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/tasks')}
            >
              <Timer size={16} className="timer-running" style={{ color: '#16a34a' }} />
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                {activeTimers.length} Timer{activeTimers.length > 1 ? 's' : ''} Active
              </Typography>
            </Box>
          )}

          {/* Report issue */}
          <Tooltip title="Report an issue">
            <IconButton
              sx={{ color: 'text.secondary' }}
              onClick={() => setReportIssueOpen(true)}
            >
              <Bug size={20} />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton 
              sx={{ color: 'text.secondary' }}
              onClick={() => setNotificationsPanelOpen(true)}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Bell size={20} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Box sx={{ ml: 2 }}>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { minWidth: 200, mt: 1 },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {user?.displayName || 'User'}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap title={user?.email} sx={{ display: 'block' }}>
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              {isAdminOrAbove(user?.role) && (
                <MenuItem onClick={() => { handleNavigation('/settings'); handleProfileMenuClose(); }}>
                  <User size={16} style={{ marginRight: 12 }} />
                  Profile
                </MenuItem>
              )}
              <MenuItem onClick={() => { setReportIssueOpen(true); handleProfileMenuClose(); }}>
                <Bug size={16} style={{ marginRight: 12 }} />
                Report an issue
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <LogOut size={16} style={{ marginRight: 12 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          // Add bottom padding for mobile nav
          pb: { xs: '80px', md: 0 },
        }}
      >
        {/* Spacer for fixed header */}
        <Box sx={{ height: { xs: 56, md: 64 } }} />
        
        {/* Active Timer Bar (shown when timer(s) are running) - desktop only */}
        {showTimerInHeader && activeTimers.length > 0 && !isMobile && <ActiveTimerBar />}

        {/* Page Content - tighter padding on mobile */}
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav onMoreClick={() => setMobileMoreOpen(true)} />}

      {/* Mobile More Menu */}
      <MobileMoreMenu
        open={mobileMoreOpen}
        onClose={() => setMobileMoreOpen(false)}
        onReportIssue={() => setReportIssueOpen(true)}
      />

      <ReportIssueModal open={reportIssueOpen} onClose={() => setReportIssueOpen(false)} />

      {/* Global Modals */}
      <GlobalModals />

      {/* 
        Logout Confirmation Dialog - Shows when user attempts logout with running timers
        
        Purpose: Inform user that their timers will be paused and allow them to cancel
        
        Features:
        - Shows count of running timers
        - Explains timers will be auto-paused and can be resumed later
        - Disables close/cancel during pause operation (prevents race conditions)
        - Shows loading spinner during pause/logout operation
        - Warning color (yellow) to indicate important action
      */}
      <Dialog
        open={showLogoutDialog}
        onClose={() => !isLoggingOut && setShowLogoutDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Active Timers Running</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have {activeTimers.filter(t => t.status === 'running').length} active timer(s) running. 
            Your timers will be paused automatically when you logout. 
            You can resume them when you login again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Cancel button - disabled during logout to prevent race conditions */}
          <Button 
            onClick={() => setShowLogoutDialog(false)} 
            disabled={isLoggingOut}
          >
            Cancel
          </Button>
          {/* Confirm button - triggers timer pause then logout */}
          <Button
            variant="contained"
            color="warning"
            onClick={confirmLogout}
            disabled={isLoggingOut}
            startIcon={isLoggingOut ? <CircularProgress size={16} /> : <LogOut size={16} />}
          >
            {isLoggingOut ? 'Pausing & Logging Out...' : 'Pause & Logout'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications Drawer */}
      <NotificationsDrawer
        open={notificationsPanelOpen}
        onClose={() => setNotificationsPanelOpen(false)}
      />
    </Box>
  );
};

export default Layout;
